import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, EnvelopeSimple, CheckCircle, XCircle, FileText } from "@phosphor-icons/react";

export default function GdprRequestPage() {
  const [params] = useSearchParams();
  const emailParam = params.get("email") || "";
  const token = params.get("token") || "";
  const slug = params.get("slug") || "";
  const isConfirmation = !!emailParam && !!token;

  const [email, setEmail] = useState(emailParam);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (slug) api.get(`/tenants/public/${slug}`).then((r) => setTenant(r.data)).catch(() => {});
  }, [slug]);

  // Auto-confirm wenn Token in der URL
  useEffect(() => {
    if (!isConfirmation) return;
    (async () => {
      setBusy(true);
      try {
        const r = await api.get(`/public/gdpr/data-export?email=${encodeURIComponent(emailParam)}&token=${encodeURIComponent(token)}${slug ? `&slug=${slug}` : ""}`);
        setStatus({
          ok: true,
          msg: `Ihre Auskunft wurde erfolgreich generiert und an ${r.data.email} versendet. Übersicht: ${r.data.summary.leads} Lead(s), ${r.data.summary.customers} Kunde(n), ${r.data.summary.invoices} Rechnung(en), ${r.data.summary.newsletter} Newsletter-Abo(s).`,
        });
      } catch (e) {
        setStatus({ ok: false, msg: e.response?.data?.detail || "Bestätigungslink ungültig oder abgelaufen." });
      } finally { setBusy(false); }
    })();
  }, [isConfirmation, emailParam, token, slug]);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus({ ok: false, msg: "Bitte gültige E-Mail-Adresse angeben" });
      return;
    }
    setBusy(true); setStatus(null);
    try {
      await api.post("/public/gdpr/data-request", { email, slug });
      setStatus({
        ok: true,
        msg: `Wir haben Ihnen eine Bestätigungs-E-Mail an ${email} gesendet. Bitte klicken Sie auf den Link in der Mail, um Ihre Datenauskunft anzufordern.`,
      });
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || "Anfrage konnte nicht gesendet werden" });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="gdpr-page">
      <div className="max-w-lg w-full bg-white border border-black/10 rounded-2xl p-8 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <FileText size={28} weight="duotone" className="text-blue-600" />
          <h1 className="font-heading text-2xl font-bold tracking-tight">DSGVO-Auskunftsersuchen</h1>
        </div>
        <p className="text-sm text-zinc-700 leading-relaxed mb-2">
          Nach Art. 15 DSGVO haben Sie das Recht, alle personenbezogenen Daten anzufordern, die
          <strong> {tenant?.name || "wir"} </strong> über Sie gespeichert hat.
        </p>
        <p className="text-xs text-zinc-500 mb-6">
          Wir senden Ihnen eine kostenlose JSON-Datei per E-Mail zu, die alle Ihre Daten enthält
          (Interessent-Einträge, Kundenkonto, Rechnungen, Newsletter, Widerrufe).
          Zur Verifizierung senden wir zunächst einen Bestätigungslink an die angegebene E-Mail-Adresse.
        </p>

        {!isConfirmation && !status?.ok && (
          <form onSubmit={submitRequest} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-zinc-700 mb-1 block">Ihre E-Mail-Adresse</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@e-mail.de"
                className="w-full h-11 px-3 border border-black/10 rounded-lg text-sm"
                data-testid="gdpr-email"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !email}
              className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="gdpr-submit"
            >
              <EnvelopeSimple size={16} weight="bold" /> {busy ? "Sende…" : "Bestätigungsmail anfordern"}
            </button>
          </form>
        )}

        {isConfirmation && busy && (
          <div className="text-sm text-zinc-600 py-4 text-center">
            Ihr Auskunftsersuchen wird verarbeitet…
          </div>
        )}

        {status && (
          <div
            className={`mt-5 p-4 rounded-lg text-sm ${status.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}
            data-testid="gdpr-feedback"
          >
            <div className="flex items-start gap-2">
              {status.ok ? <CheckCircle size={18} weight="bold" className="mt-0.5" /> : <XCircle size={18} weight="bold" className="mt-0.5" />}
              <div>{status.msg}</div>
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-black/5 text-[10px] text-zinc-400 leading-relaxed">
          Diese Auskunft wird gemäß Art. 15 DSGVO kostenlos innerhalb weniger Minuten erstellt.
          Bei Fragen wenden Sie sich bitte an {tenant?.email || "unseren Datenschutzbeauftragten"}.
        </div>
      </div>
    </div>
  );
}
